import { Navbar as CoreUINavbar, Icon } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import { useCallback, useContext, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import betaLogo from '/public/MAESTRO beta Logo UI.svg';
import { useAuth } from '../hooks/useAuth';
import { useIsDashboardMode } from '../hooks/useIsDashboardMode';
import { useMcpBridge } from '../mcp/McpBridgeProvider';
import { GuardianContext } from './GuardianProvider';

const QUICKSTART_DOCUMENTATION_URL =
  'https://docs.google.com/document/d/1tD8GnRj611b55VeAv3I8ScEjCJ2bxOh_tPOujeJB7Do/edit?usp=sharing';

const Logo = styled.img`
  height: 2.143rem;
`;

/** Blur fading out under the navbar, so content scrolling behind it dissolves rather than cuts. */
const Backdrop = styled.div`
  mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(var(--header-height) + 2rem);
  backdrop-filter: blur(30px);
  z-index: 1;
  pointer-events: none;
`;

const NavbarContainer = styled(CoreUINavbar)<{ $opaque: boolean }>`
  background-color: ${({ $opaque, theme }) => ($opaque ? theme.navbarBackground : 'unset')};
  border: 0;
  position: fixed;
  z-index: 2;
  width: 100vw;

  /* An overlaid pseudo-element, not a border (which would add a pixel and nudge the logo on
     every mode change) and not an inset shadow (which paints under the opaque right actions). */
  ${({ $opaque, theme }) =>
    $opaque &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: auto 0 0 0;
        height: 1px;
        /* A separator, not an outline: the border token is #4A4A4A on a #121219 navbar. */
        background-color: ${theme.backgroundLevel2};
        pointer-events: none;
        z-index: 1;
      }
    `}

  /*
   * core-ui renders dropdown triggers with \`height: auto\` (~39px, content
   * height) while the navbar itself is \`navbarHeight\` (3rem / 42px).
   */
  .sc-button {
    background-color: ${({ theme }) => theme.navbarBackground};
  }

  .sc-dropdown .trigger {
    height: 3rem;
    background-color: ${({ theme }) => theme.navbarBackground};
  }
`;

export const Navbar = () => {
  const { isAuthenticated, userName, signOut } = useAuth();
  const theme = useTheme();
  const isDashboardMode = useIsDashboardMode();
  const { isInIframe } = useMcpBridge();
  const { isSidebarOpen, setIsSidebarOpen, setConversationId } = useContext(GuardianContext);

  const toggleSidebar = useCallback(() => setIsSidebarOpen((prev) => !prev), [setIsSidebarOpen]);
  const handleNewChat = useCallback(() => {
    setConversationId(uuidv4());
    setIsSidebarOpen(false);
  }, [setConversationId, setIsSidebarOpen]);

  const rightActions = useMemo(() => {
    if (!isAuthenticated) return [];

    if (isInIframe) {
      return [
        {
          type: 'custom' as const,
          render: () => (
            <Button
              style={{ backgroundColor: 'transparent' }}
              onClick={toggleSidebar}
              icon={<Icon name="Sidebar" color={isSidebarOpen ? theme.selectedActive : theme.textPrimary} />}
              tooltip={{ overlay: isSidebarOpen ? 'Close history' : 'Open history' }}
            />
          ),
        },
        {
          type: 'custom' as const,
          render: () => (
            <Button
              style={{ backgroundColor: 'transparent' }}
              onClick={handleNewChat}
              icon={<Icon name="Pencil" color={theme.textPrimary} />}
              tooltip={{ overlay: 'Start new conversation' }}
            />
          ),
        },
      ];
    }

    return [
      {
        type: 'custom' as const,
        render: () => (
          <Button
            label="QuickStart"
            onClick={() => window.open(QUICKSTART_DOCUMENTATION_URL, '_blank')}
            icon={<Icon name="Bookopen" />}
            style={{
              width: '8rem',
              color: theme.textPrimary,
              cursor: 'pointer',
            }}
            tooltip={{ overlay: 'Open QuickStart external documentation' }}
          />
        ),
      },
      {
        text: userName,
        caret: true,
        type: 'dropdown' as const,
        items: [
          {
            // core-ui requires a string but we always pass ReactNode to render an icon
            label: (
              <>
                <Icon name="Log-out" />
                &nbsp;Sign out
              </>
            ) as any,
            onClick: signOut,
          },
        ],
      },
    ];
  }, [
    isAuthenticated,
    isInIframe,
    isSidebarOpen,
    toggleSidebar,
    handleNewChat,
    userName,
    signOut,
    theme.textPrimary,
    theme.selectedActive,
  ]);

  return (
    <>
      <NavbarContainer
        $opaque={isDashboardMode}
        logo={<Logo src={betaLogo} alt="Guardian Logo" />}
        rightActions={rightActions}
      />
      {/* No blur in dashboard mode: the header pinned below it read as a rendering fault. */}
      {!isDashboardMode && <Backdrop />}
    </>
  );
};
